import { Box, Column } from "@stenajs-webui/core";
import {
  ActionMenuItem,
  stenaHistory,
  stenaPrint,
} from "@stenajs-webui/elements";
import { ActionMenuSecondaryButton } from "@stenajs-webui/panels";
import {
  type FC,
  lazy,
  type ReactNode,
  Suspense,
  useRef,
  useState,
} from "react";

import { CopyActionMenuItem } from "../../../../common-ui/CopyActionMenuItem";
import { FreightPortalControlledPopover } from "../../../../common-ui/popover/FreightPortalControlledPopover";
import { isBookingClaimable } from "../../../../common/claims/claimsUtils";
import { stringifyBooking } from "../../../../common/formatters/bookingStringFormatter";
import type { DetailedBooking } from "../../../../common/graphql/fragments/gql/DetailedBooking";
import { useAppNavigate } from "../../../../common/hooks/useAppNavigate";
import { useHasPermission } from "../../../../common/hooks/useHasPermission";
import { useLocalDateFormat } from "../../../../common/hooks/useLocalDateFormat";
import { modalRouteCreators } from "../../../../common/routes/AppRoutes";
import {
  type TrackerCategory,
  trackEvent,
} from "../../../../common/tracking/trackerService";
import { Permission } from "../../../../services/authentication/utils/authUtils";
import { ActionMenuClaimsLink } from "../../grid/components/options-table-cell/ActionMenuClaimsLink";
import { MailBookingActionMenuItem } from "../../grid/components/options-table-cell/MailBookingActionMenuItem";
import { SendBookingActionMenuItem } from "../../grid/components/options-table-cell/SendBookingActionMenuItem";
import { SwitchBookingActionMenuItem } from "../../grid/components/options-table-cell/SwitchBookingActionMenuItem";
import { ShareBookingPanel } from "../../share/components/ShareBookingPanel";
import { shouldShowShareBooking } from "../../share/util/shareUtil";
import { isBookingSwitchable } from "../../switch/utils/switchUtils";
import { HistoryPanel } from "./HistoryPanel";

const DetailsPrintButton = lazy(
  () => import("../../../../common/pdf/DetailsPrintButton"),
);

interface BookingActionMenuButtonProps {
  booking: DetailedBooking;
  disabled?: boolean;
  edited?: boolean;
  renderItems?: (close: () => void) => ReactNode;
}

const trackerCategory: TrackerCategory = "Details";

type BookingActionModalVariant = "history" | "share";

export const ACTION_MENU_WIDTH = "22rem";
export const ACTION_MENU_PLACEMENT = "bottom-end";

export const BookingActionMenuButton: FC<BookingActionMenuButtonProps> = ({
  booking,
  disabled,
  edited,
  renderItems,
}) => {
  const [modal, setModal] = useState<BookingActionModalVariant | null>(null);
  const columnRef = useRef<HTMLDivElement>(null);

  const vehicleRegNumber = booking.vehicleRegNo || booking.trailerRegNo;

  const getHasPermission = useHasPermission();
  const dateFormat = useLocalDateFormat();
  const { replace } = useAppNavigate();

  const showSwitchOption =
    getHasPermission(Permission.EditBookings) && isBookingSwitchable(booking);
  const showClaimsOptions = isBookingClaimable(booking.bookingStatus.status);

  const onClickHistory = () => {
    trackEvent({ category: trackerCategory, action: "History" });
    setModal("history");
  };

  const closeModal = () => setModal(null);

  const onClickMail = () => {
    trackEvent({ category: trackerCategory, action: "Mail" });
  };

  const onClickCopy = () =>
    trackEvent({ category: trackerCategory, action: "Copy to clipboard" });

  const onClickClaims = () => {
    trackEvent({ category: trackerCategory, action: "Make claim" });
  };

  const onClickShare = () => {
    setModal("share");
    trackEvent({
      category: trackerCategory,
      action: "Share booking (menu item)",
    });
  };

  const onClickSwitch = () => {
    replace(modalRouteCreators.bookingSwitch({ bookingNo: booking.bookingNo }));
    trackEvent({ category: trackerCategory, action: "Switch" });
  };

  return (
    <Column ref={columnRef}>
      <FreightPortalControlledPopover
        placement={"bottom-end"}
        open={modal != null}
        onRequestClose={closeModal}
        renderTrigger={(props) => (
          <Column {...props}>
            <ActionMenuSecondaryButton
              label={"Booking actions"}
              disabled={disabled}
              placement={ACTION_MENU_PLACEMENT}
              menuWidth={ACTION_MENU_WIDTH}
              renderItems={(close) => (
                <>
                  <Suspense
                    fallback={
                      <ActionMenuItem
                        leftIcon={stenaPrint}
                        label={"Print booking"}
                        loading
                      />
                    }
                  >
                    <DetailsPrintButton
                      booking={booking}
                      dateFormat={dateFormat}
                    />
                  </Suspense>
                  <CopyActionMenuItem
                    onClick={onClickCopy}
                    getValue={() => stringifyBooking(booking, dateFormat)}
                    disabled={disabled}
                  />
                  <MailBookingActionMenuItem
                    booking={booking}
                    onClick={() => {
                      close();
                      onClickMail();
                    }}
                    disabled={disabled || edited}
                  />
                  {shouldShowShareBooking(
                    vehicleRegNumber,
                    booking.bookingStatus.status,
                    booking.bookingQuayStatus?.status,
                  ) && (
                    <Box display={["flex", null, null, null, "none"]}>
                      <SendBookingActionMenuItem
                        onClick={() => {
                          close();
                          onClickShare();
                        }}
                        disabled={disabled}
                      />
                    </Box>
                  )}
                  {showSwitchOption && (
                    <SwitchBookingActionMenuItem
                      bookingNo={booking.bookingNo}
                      onClick={() => {
                        close();
                        onClickSwitch();
                      }}
                    />
                  )}
                  {showClaimsOptions && (
                    <ActionMenuClaimsLink
                      disabled={disabled}
                      bookingNo={booking.bookingNo}
                      onClick={() => {
                        close();
                        onClickClaims();
                      }}
                    />
                  )}
                  <ActionMenuItem
                    label={"History"}
                    leftIcon={stenaHistory}
                    onClick={() => {
                      close();
                      onClickHistory();
                    }}
                    disabled={disabled}
                  />
                  {renderItems?.(close)}
                </>
              )}
            />
          </Column>
        )}
      >
        {modal === "share" ? (
          <ShareBookingPanel
            onClose={closeModal}
            bookingNo={booking.bookingNo}
            vehicleRegNo={vehicleRegNumber}
            trackerCategory={trackerCategory}
          />
        ) : modal === "history" ? (
          <HistoryPanel bookingNo={booking.bookingNo} onClose={closeModal} />
        ) : null}
      </FreightPortalControlledPopover>
    </Column>
  );
};
